import { useEffect, useState } from 'react';
import { RxDetail } from '../components/PrescribePanel';
import { api, type PatientSummary } from '../api';
import { useI18n } from '../i18n';
import { PatientPicker } from '../components/PatientPicker';
import { EmptyState, ErrorState, Field, Modal, Skeleton, Spinner, Tabs } from '../components';
import { describe } from '../errors';

/**
 * Prescribing (spec §8).
 *
 * The interface makes the safety state impossible to miss:
 *
 *   - the mode banner says whether checking is clinical or synthetic
 *   - a blocked prescription cannot be issued and has no valid print
 *   - a stale check (the record changed) refuses issuance until re-run
 *   - there is no "override" control anywhere on this page
 */
export function Prescriptions({
  clinicId,
  canIssue,
  canManageTemplates,
}: {
  clinicId: string;
  canIssue: boolean;
  canManageTemplates: boolean;
}) {
  const { t, formatDate, personName } = useI18n();
  const [tab, setTab] = useState<'list' | 'templates'>('list');
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.prescriptions>>['prescriptions'] | null>(null);
  const [mode, setMode] = useState<{ mode: string; clinical: boolean; warning: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setRows(null);
    Promise.all([api.prescriptions(), api.rxMode()])
      .then(([r, m]) => {
        setRows(r.prescriptions);
        setMode(m);
      })
      .catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  const stateTone: Record<string, string> = {
    issued: 'ok',
    checked: 'info',
    blocked: 'danger',
    draft: '',
    cancelled: 'warn',
  };

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.rx.title}</h1>
        </div>
        <button type="button" className="btn" onClick={() => setCreating(true)}>
          + {t.rx.new}
        </button>
      </div>

      {/* The provenance of every verdict on this page, stated up front. */}
      {mode && !mode.clinical ? (
        <div className="notice warn" style={{ marginBottom: 14 }} role="alert">
          <span>{t.rx.syntheticNotice}</span>
        </div>
      ) : null}

      <Tabs
        value={tab}
        onChange={(v: string) => setTab(v as typeof tab)}
        tabs={[
          { value: 'list', label: t.rx.title },
          { value: 'templates', label: t.rx.templates },
        ]}
      />

      {tab === 'templates' ? <Templates clinicId={clinicId} canPublish={canManageTemplates} /> : null}

      {tab === 'list' ? (
      <div className="card">
        {rows === null ? (
          <Skeleton rows={5} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.rx.noPrescriptions} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.finance.patient}</th>
                  <th>{t.rx.items}</th>
                  <th>{t.rx.state}</th>
                  <th>{t.finance.issued}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => setOpenId(r.id)}>
                    <td>
                      <strong>{personName(r.patient)}</strong>
                      <span className="ltr-value" style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>
                        {r.patient.patientNumber}
                      </span>
                    </td>
                    <td>{r.itemCount}</td>
                    <td>
                      <span className={`badge ${stateTone[r.state] ?? ''}`}>
                        {(t.rx.states as Record<string, string>)[r.state] ?? r.state}
                      </span>
                    </td>
                    <td>{r.issuedAt ? formatDate(r.issuedAt) : '—'}</td>
                    <td style={{ textAlign: 'end' }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenId(r.id);
                        }}
                      >
                        {t.patients.view}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      ) : null}

      {creating ? (
        <NewPrescription
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setNonce((n) => n + 1);
            setOpenId(id);
          }}
        />
      ) : null}

      {openId ? (
        <RxDetail
          id={openId}
          canIssue={canIssue}
          onClose={() => setOpenId(null)}
          onChanged={() => setNonce((n) => n + 1)}
        />
      ) : null}
    </>
  );
}

/*
 * The detail panel is the SAME component the patient file uses. This page had
 * its own copy, which never grew the edit and discard actions the patient-file
 * one has — so a prescription blocked by the safety check could be edited from
 * the patient's record and not from here, and looked uneditable.
 */

function NewPrescription({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t, locale } = useI18n();
  const [, setPatients] = useState<PatientSummary[]>([]);
  const [patientId, setPatientId] = useState('');
  const [drugs, setDrugs] = useState<Awaited<ReturnType<typeof api.drugs>>['drugs']>([]);
  const [templates, setTemplates] = useState<Awaited<ReturnType<typeof api.rxTemplates>>['templates']>([]);
  const [items, setItems] = useState([
    { medicineText: '', dose: '1 tablet', route: 'oral', frequency: 'three times daily', duration: '5 days', instructions: '' },
  ]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([api.patients({ limit: 100 }), api.drugs(), api.rxTemplates()]).then(([p, d, tp]) => {
      setPatients(p.patients);
      if (p.patients[0]) setPatientId(p.patients[0].id);
      setDrugs(d.drugs);
      setTemplates(tp.templates);
    });
  }, []);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.createPrescription({
        patientId,
        items: items.filter((i) => i.medicineText.trim().length > 0),
      });
      onCreated(r.id);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t.rx.new}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void submit()}
            disabled={busy || !patientId || items.every((i) => !i.medicineText.trim())}
          >
            {busy ? <><Spinner /> {t.patients.saving}</> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}

        <Field label={t.finance.patient} required>
          <PatientPicker value={patientId} onChange={(id) => setPatientId(id)} />
        </Field>

        <Field label={t.rx.useTemplate} hint={t.rx.templateNote}>
          <select
            value=""
            onChange={(e) => {
              const tpl = templates.find((x) => x.id === e.target.value);
              if (tpl) {
                setItems(
                  tpl.items.map((i) => ({
                    medicineText: i.medicineText,
                    dose: i.dose,
                    route: i.route,
                    frequency: i.frequency,
                    duration: i.duration,
                    instructions: i.instructions ?? '',
                  })),
                );
              }
            }}
          >
            <option value="">—</option>
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {locale === 'ar' ? tpl.nameAr : tpl.nameEn}
              </option>
            ))}
          </select>
        </Field>

        <div className="section-label">{t.rx.items}</div>
        {items.map((item, idx) => (
          <div key={idx} className="card" style={{ padding: 12 }}>
            <div className="stack" style={{ gap: 10 }}>
              <Field label={t.rx.medicine} required>
                <input
                  list="drug-list"
                  value={item.medicineText}
                  onChange={(e) => {
                    const next = [...items];
                    next[idx] = { ...item, medicineText: e.target.value };
                    setItems(next);
                  }}
                />
              </Field>
              <div className="grid-2">
                <Field label={t.rx.dose}>
                  <input
                    value={item.dose}
                    onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...item, dose: e.target.value };
                      setItems(next);
                    }}
                  />
                </Field>
                <Field label={t.rx.frequency}>
                  <input
                    value={item.frequency}
                    onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...item, frequency: e.target.value };
                      setItems(next);
                    }}
                  />
                </Field>
                <Field label={t.rx.route}>
                  <input
                    value={item.route}
                    onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...item, route: e.target.value };
                      setItems(next);
                    }}
                  />
                </Field>
                <Field label={t.rx.duration}>
                  <input
                    value={item.duration}
                    onChange={(e) => {
                      const next = [...items];
                      next[idx] = { ...item, duration: e.target.value };
                      setItems(next);
                    }}
                  />
                </Field>
              </div>
            </div>
          </div>
        ))}

        <datalist id="drug-list">
          {drugs.map((d) => (
            <option key={d.id} value={d.nameEn} />
          ))}
        </datalist>

        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() =>
            setItems([
              ...items,
              { medicineText: '', dose: '', route: 'oral', frequency: '', duration: '', instructions: '' },
            ])
          }
        >
          + {t.rx.addItem}
        </button>
      </div>
    </Modal>
  );
}

/* ----------------------------------------------------------- templates -- */

type TemplateRow = Awaited<ReturnType<typeof api.rxTemplates>>['templates'][number];
type TemplateItem = {
  medicineText: string;
  dose: string;
  route: string;
  frequency: string;
  duration: string;
  quantity?: string;
  instructions?: string;
};

const BLANK_ITEM: TemplateItem = { medicineText: '', dose: '', route: 'oral', frequency: '', duration: '' };

/**
 * A dentist's own prescribing habits, written down once.
 *
 * Templates could be saved from inside a prescription and could not be seen,
 * changed or written anywhere else — so "add a template" was an act that
 * happened as a side effect of prescribing, and a mistake in one lived
 * forever. This is the place they live. Anyone who can prescribe can keep
 * personal ones; publishing to the whole clinic needs the permission that
 * says so, and the form says which is which.
 */
function Templates({ clinicId, canPublish }: { clinicId: string; canPublish: boolean }) {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState<TemplateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<TemplateRow | 'new' | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setRows(null);
    api.rxTemplates().then((r) => setRows(r.templates)).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        <button type="button" className="btn" onClick={() => setEditing('new')}>
          + {t.rx.newTemplate}
        </button>
      </div>

      <div className="card">
        {rows === null ? (
          <Skeleton rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.rx.noTemplates} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.rx.templateName}</th>
                  <th>{t.rx.items}</th>
                  <th>{t.rx.templateScope}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((tpl) => (
                  <tr key={tpl.id}>
                    <td>
                      <strong>{locale === 'ar' ? tpl.nameAr : tpl.nameEn}</strong>
                      {tpl.description ? (
                        <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>{tpl.description}</span>
                      ) : null}
                    </td>
                    <td>
                      {(tpl.items as TemplateItem[]).map((i) => i.medicineText).join(', ')}
                    </td>
                    <td>
                      <span className={tpl.scope === 'clinic' ? 'badge brand' : 'badge'}>
                        {(t.rx.scopeBadge as Record<string, string>)[tpl.scope] ?? tpl.scope}
                      </span>
                    </td>
                    <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                      {tpl.editable ? (
                        <button type="button" className="btn-link" onClick={() => setEditing(tpl)}>
                          {t.remove.edit}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <TemplateEditor
          template={editing === 'new' ? null : editing}
          canPublish={canPublish}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}
    </>
  );
}

function TemplateEditor({
  template,
  canPublish,
  onClose,
  onDone,
}: {
  template: TemplateRow | null;
  canPublish: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [nameEn, setNameEn] = useState(template?.nameEn ?? '');
  const [nameAr, setNameAr] = useState(template?.nameAr ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [scope, setScope] = useState<'personal' | 'clinic'>(template?.scope === 'clinic' ? 'clinic' : 'personal');
  const [items, setItems] = useState<TemplateItem[]>(
    template
      ? (template.items as TemplateItem[]).map((i) => ({
          ...BLANK_ITEM,
          ...i,
          quantity: i.quantity ?? undefined,
          instructions: i.instructions ?? undefined,
        }))
      : [{ ...BLANK_ITEM }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete = items.every((i) => i.medicineText.trim() && i.dose.trim() && i.frequency.trim() && i.duration.trim());

  const setItem = (index: number, patch: Partial<TemplateItem>) =>
    setItems((l) => l.map((x, i) => (i === index ? { ...x, ...patch } : x)));

  async function save(archive = false) {
    setBusy(true);
    setError(null);
    try {
      if (template) {
        await api.updateRxTemplate(template.id, archive ? { archived: true } : { nameEn, nameAr: nameAr || nameEn, description: description || null, items });
      } else {
        await api.createRxTemplate({ nameEn, nameAr: nameAr || undefined, description: description || undefined, items, scope });
      }
      onDone();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={template ? t.rx.editTemplate : t.rx.newTemplate}
      onClose={onClose}
      footer={
        <>
          {template ? (
            <button type="button" className="btn btn-danger" disabled={busy} onClick={() => void save(true)}>
              {t.rx.archiveTemplate}
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !nameEn.trim() || !complete}
            onClick={() => void save()}
          >
            {busy ? <Spinner /> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}

        <div className="grid-2">
          <Field label={t.rx.templateName} required>
            <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} autoFocus />
          </Field>
          <Field label={`${t.rx.templateName} (العربية)`}>
            <input value={nameAr} onChange={(e) => setNameAr(e.target.value)} dir="rtl" />
          </Field>
        </div>
        <Field label={t.rx.templateDescription} hint={t.patients.optional}>
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        {!template ? (
          <Field label={t.rx.templateScope} hint={t.rx.clinicScopeNeeds}>
            <select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
              <option value="personal">{t.rx.personalScope}</option>
              <option value="clinic" disabled={!canPublish}>
                {t.rx.clinicScope}
              </option>
            </select>
          </Field>
        ) : null}

        <div className="section-label">{t.rx.items}</div>
        {items.map((item, index) => (
          <div key={index} className="card" style={{ padding: 12 }}>
            <Field label={t.rx.medicine} required>
              <input value={item.medicineText} onChange={(e) => setItem(index, { medicineText: e.target.value })} />
            </Field>
            <div className="grid-2" style={{ marginTop: 10 }}>
              <Field label={t.rx.dose} required>
                <input value={item.dose} onChange={(e) => setItem(index, { dose: e.target.value })} />
              </Field>
              <Field label={t.rx.route} required>
                <input value={item.route} onChange={(e) => setItem(index, { route: e.target.value })} />
              </Field>
              <Field label={t.rx.frequency} required>
                <input value={item.frequency} onChange={(e) => setItem(index, { frequency: e.target.value })} />
              </Field>
              <Field label={t.rx.duration} required>
                <input value={item.duration} onChange={(e) => setItem(index, { duration: e.target.value })} />
              </Field>
              <Field label={t.rx.quantity} hint={t.patients.optional}>
                <input value={item.quantity ?? ''} onChange={(e) => setItem(index, { quantity: e.target.value || undefined })} />
              </Field>
              <Field label={t.rx.instructions} hint={t.patients.optional}>
                <input value={item.instructions ?? ''} onChange={(e) => setItem(index, { instructions: e.target.value || undefined })} />
              </Field>
            </div>
            {items.length > 1 ? (
              <button
                type="button"
                className="btn-link danger"
                style={{ marginTop: 10 }}
                onClick={() => setItems((l) => l.filter((_, i) => i !== index))}
              >
                {t.rx.removeItem}
              </button>
            ) : null}
          </div>
        ))}
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setItems((l) => [...l, { ...BLANK_ITEM }])}>
          {t.rx.addItem}
        </button>
      </div>
    </Modal>
  );
}
